<template>
  <div ref="actionPlanSlot" class="info-slot-container position-relative">
    <button
      v-if="areDocuments || isDocumentListVisible && $can('Read', 'DocumentActionPlan')"
      class="toggle-button" @click="onToggleClick">
      PDF
    </button>
    <!-- Document List -->
    <div v-if="isDocumentListVisible" class="document-list">
      <b-form-input
        data-cy="search"
        class="d-inline-block mr-1"
        :placeholder="$t('table.search')"
        v-model="searchQuery"
        @input="onSearchInput"
      />
      <table class="table">
        <thead>
        <tr>
          <th v-for="field in tableColumns" :key="field.key" class="capitalize-first">
            {{ field.label }}
          </th>
        </tr>
        </thead>
        <tbody v-for="(document) in documents">
        <tr
          class="cursor-pointer"
          draggable="true"
          @dragstart="setSelectedWidget('document_action_plan', document)"
        >
          <td>
            {{ '⠿ ' + document.name }}
          </td>
        </tr>
        </tbody>
      </table>
    </div>
    <div class="p-2" v-if="actionPlans.length && !actionPlanSelectedId">
      <p class="lead text-center font-medium-3 mb-2">{{ $tc("action_plan.title", 2) }}</p>
      <div class="action-plan-list d-flex flex-wrap">
        <div
          class="action-plan-item d-flex align-items-center justify-content-center p-1 mr-1 mb-1"
          v-for="(actionPlan, index) in actionPlans"
          :key="index"
          @click="onActionPlanSelected(actionPlan)"
        >
          <span class="lead">{{ actionPlan.name }}</span>
        </div>
      </div>
    </div>
    <div class="p-1" v-if="actionPlanSelectedId">
      <!--<action-plan-list
        v-if="currentLocation !== 'action_plan_logs' && this.source !== SOURCES.MAIN_SEARCH"
        class="mb-2"
        :action-plan-id="actionPlanSelectedId"/>-->
      <div class="action-plan-viewer-container mb-2">
        <!--<div class="d-flex justify-content-end align-items-center mb-1">
          <small v-if="actionPlanRefreshError" class="text-danger mr-1">
            {{ actionPlanRefreshError }}
          </small>
          <b-button
            v-if="config.uuid"
            variant="outline-primary"
            size="sm"
            :disabled="isRefreshingActionPlan"
            @click="refreshActionPlan()"
          >
            <b-spinner v-if="isRefreshingActionPlan" small class="mr-25"/>
            <feather-icon v-else icon="RefreshCwIcon" size="16" class="mr-25"/>
            {{ $t("actions.refresh") }}
          </b-button>
        </div>-->
        <b-button variant="primary"
                  size="sm"
                  v-for="activeActionPlan in activeActionPlans"
                  :key="activeActionPlan.id"
                  @click="onClickActiveActionPlan(activeActionPlan)"
                  class="mr-1 mb-1">
          {{ activeActionPlan.name }}
        </b-button>
      </div>
      <div
        ref="actionPlanViewerContainer"
        class="action-plan-viewer-container"
        :style="container"
      >
        <action-plan-viewer
          :key="actionPlanViewerKey"
          v-model="actionPlanHistory"
          :action-plan-id="actionPlanSelectedId"
          :parent-container="$el"
          :current-location="currentLocation"
          :config="config"
          :data="slotData"
          :preview="preview"
          :validating="validating"
          @validate-success="$emit('validate-success')"
          @rendered="scrollActionPlanToBottom"
          @updated="scrollActionPlanToBottom"
        />
      </div>
    </div>
  </div>
</template>

<script>
import axios from "@axios";
import store from "@/store";
import ActionPlanViewer from "@/components/ActionPlanViewer";
import {SLOT_TYPES, SOURCES} from "@/config/layoutConfig";
import {v4 as uuidv4} from "uuid";
import LayoutApi from "@/libs/LayoutApi";
import draggable from "vuedraggable";
import layoutMixin from "@/mixins/layoutMixin";
import {BCollapse} from "bootstrap-vue";
import {debounce} from 'lodash';
import ActionPlanList from "@/components/ActionPlanViewer/ActionPlanList.vue";
import {EventBus} from "@/libs/event-bus";

const layoutApi = new LayoutApi(store);

export default {
  mixins: [layoutMixin],
  props: {
    data: Object,
    preview: {
      type: Boolean,
      default: false,
    },
    validating: {
      type: Boolean,
      default: false,
    },
    currentLocation: {
      type: String,
      default: "grid",
    }
  },
  components: {
    ActionPlanList,
    ActionPlanViewer,
    draggable,
    BCollapse,
  },
  data() {
    return {
      actionPlans: [],
      activeActionPlans: [],
      actionPlanSelectedId: null,
      actionPlanHistory: null,
      alarmData: null,
      slotData: null,
      source: null,
      config: {
        userId: null,
        alarmId: null,
        alarmTypeId: null,
        uuid: null,
        alarm: null
      },
      resizeObserver: null,
      elementWidth: 'auto',
      elementHeight: 'auto',
      isDocumentListVisible: false,
      areDocuments: false,
      documents: [],
      tableColumns: [
        {label: this.$t(`action_plan.documentTable.name`), key: 'name', sortable: true},
      ],
      SLOT_TYPES,
      SOURCES,
      searchQuery: '',
      actionPlanViewerKey: 0,
      isRefreshingActionPlan: false,
      actionPlanRefreshError: null,
      actionPlanCheckpointChannel: null,
    };
  },
  created() {
    this.slotInfo = this.data;
    this.slotData = this.slotInfo.data;
    this.source = this.slotInfo.data.in_app_source;
  },
  async mounted() {
    this.fetchActiveActionPlans().then((resp) => {
      const {data: data} = resp.data;
      this.activeActionPlans = data;
    });

    if (SOURCES.MAIN_SEARCH !== this.source) {
      this.setObserver();
    }

    const {data: data} = await this.fetchActionPlans();
    const _data = data.data;

    if ([SOURCES.MAIN_SEARCH, SOURCES.COMMAND].includes(this.source)) {
      if (_data) {
        const isEmpty = Array.isArray(_data.data) && _data.data.length === 0;
        this.actionPlanSelectedId = this.slotData.id_action_plan;
        this.actionPlanHistory = !isEmpty ? _data.data : {};
        this.config = {...this.config, ..._data.config || {}};
      } else {
        this.actionPlanSelectedId = this.slotData.id_action_plan;
      }
    } else {
      if (_data.type === "action_plan_list") {
        this.actionPlans = _data.data;
      } else {
        if (_data) {
          const isEmpty = Object.keys(_data.data).length === 0;
          this.actionPlanSelectedId = _data.data.id || this.slotData.id_action_plan;
          this.actionPlanHistory = !isEmpty ? _data.data : {};
          this.config = {...this.config, ..._data.data.config || {}};
        } else {
          this.actionPlanSelectedId = this.slotData.id_action_plan;
        }
      }
    }

    this.config.alarm = this.slotData.alarm;
    if (!this.config.uuid) {
      this.config.uuid = this.slotData.uuid_action_plan_log
        ? this.slotData.uuid_action_plan_log
        : uuidv4();
    }

    this.setActionPlanIdInSlotData({
      id_action_plan: this.actionPlanSelectedId,
      uuid_action_plan_log: this.config.uuid,
    });

    this.config.userId = this.user.id;
    this.config.alarmId = this.slotData.id || this.slotData.id_alarm || null;
    this.config.alarmTypeId = this.slotData.id_alarm_type
      || this.slotData.alarm?.id_alarm_type
      || null;

    // Subscribe to action plan checkpoints
    this.subscribeToActionPlanCheckpoints();

    //get the documents
    try {
      store.dispatch('action_plan_document/getAll', {}).then(result => {
        if (result && result.data) {
          const tenantsData = result.data;
          this.documents = tenantsData;
          const tenantExists = this.documents.find(doc => doc.tenant_id === this.slotData.tenant.id);
          if (tenantExists) {
            this.areDocuments = true;
          }
        } else {
          console.error('No data found in the result');
          this.areDocuments = false;
          this.documents = [];
        }

      });
    } catch (error) {
      console.error('Error fetching action plan documents:', error);
    }
  },
  beforeDestroy() {
    this.unsubscribeFromActionPlanCheckpoints();
    this.unsetObserver();
  },
  computed: {
    user() {
      return store.getters["user/getUser"];
    },
    container() {
      if (this.currentLocation === 'action_plan_logs') {
        return "";
      }
      return SOURCES.MAIN_SEARCH !== this.source ? `max-height: calc(${this.elementHeight}px - 110px)` : 'height: auto'
    }
  },
  methods: {
    subscribeToActionPlanCheckpoints() {
      if (!window.Echo || !this.config.uuid) return;

      this.actionPlanCheckpointChannel = window.Echo
        .private(`action-plan-checkpoints-${this.config.uuid}`)
        .listen(
          ".action-plan.checkpoint-updated",
          this.onActionPlanCheckpointUpdated,
        );
    },
    unsubscribeFromActionPlanCheckpoints() {
      if (!this.actionPlanCheckpointChannel) return;

      this.actionPlanCheckpointChannel.stopListening(
        ".action-plan.checkpoint-updated",
        this.onActionPlanCheckpointUpdated,
      );
      this.actionPlanCheckpointChannel = null;
    },
    onActionPlanCheckpointUpdated(event) {
      const isCurrentLog = String(event.action_plan_log_uuid)
        === String(this.config.uuid);
      const wasUpdatedByCurrentUser = Number(event.updated_by_user_id)
        === Number(this.user.id);

      if (!isCurrentLog || wasUpdatedByCurrentUser) return;

      this.refreshActionPlan(false);
    },
    async refreshActionPlan(showError = true) {
      if (this.isRefreshingActionPlan || !this.config.uuid) return;

      this.isRefreshingActionPlan = true;
      this.actionPlanRefreshError = null;

      try {
        const {data: response} = await axios.get(
          `/v1/action_plan/logs/${this.config.uuid}`,
        );
        const actionPlanLog = response?.data;
        const log = actionPlanLog?.log;

        if (!log || typeof log !== "object") return;

        this.actionPlanSelectedId = actionPlanLog.id_action_plan
          || this.actionPlanSelectedId;
        this.actionPlanHistory = JSON.parse(JSON.stringify(log));
        this.actionPlanViewerKey += 1;
      } catch (error) {
        if (showError) {
          this.actionPlanRefreshError = "No fue posible actualizar el plan de acción.";
        }
      } finally {
        this.isRefreshingActionPlan = false;
      }
    },
    scrollActionPlanToBottom() {
      this.$nextTick(() => {
        [
          this.$refs.actionPlanViewerContainer,
          this.$refs.actionPlanSlot,
        ].filter(Boolean).forEach((container) => {
          container.scrollTop = container.scrollHeight;
        });
      });
    },
    setObserver() {
      this.resizeObserver = new ResizeObserver(entries => {
        for (let entry of entries) {
          this.elementWidth = entry.contentRect.width;
          this.elementHeight = entry.contentRect.height;
        }
      });

      this.resizeObserver.observe(this.$refs.actionPlanSlot);
    },
    unsetObserver() {
      if (this.resizeObserver) {
        this.resizeObserver.unobserve(this.$refs.actionPlanSlot)
      }
    },
    fetchActionPlans() {
      return axios.get("/v1/action_plan/find-action-plan", {
        params: {
          id_alarm: this.slotData.id || null,
          alarm_type: this.slotData.type || null,
          id_tenant: this.slotData.tenant ? this.slotData.tenant.id : null,
          id_action_plan: this.slotData.id_action_plan || null,
          uuid: this.slotData.uuid_action_plan_log || null,
          check_for_logs: true,
        },
      });
    },
    async fetchActiveActionPlans() {
      const idAlarm = this.slotData.id || null;

      return axios.get("/v1/action_plan/get-active-action-plans", {
        params: {
          id_alarm: idAlarm,
        },
      });
    },
    onClickActiveActionPlan(actionPlan) {
      const title = this.$t('action_plan.replace_modal.title', {name: actionPlan.name});
      const description = this.$t('action_plan.replace_modal.description');

      this.$swal({
        title: title,
        text: description,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: `${this.$t("actions.accept")}`,
        cancelButtonText: `${this.$t("actions.cancel")}`,
        customClass: {
          confirmButton: "btn btn-primary text-capitalize",
          cancelButton: "btn btn-outline-danger ml-1 text-capitalize",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          EventBus.emit(`add_action_plan_${this.actionPlanSelectedId}`, {actionPlanId: actionPlan.id})
        }
      }).finally(() => {
        this.selectedActionPlan = null;
      });
    },
    onActionPlanSelected(actionPlan) {
      const title = this.$t('action_plan.start_modal.title', {name: actionPlan.name});

      this.$swal({
        title: title,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: `${this.$t("accept")}`,
        cancelButtonText: `${this.$t("actions.cancel")}`,
        customClass: {
          confirmButton: "btn btn-primary text-capitalize",
          cancelButton: "btn btn-outline-danger ml-1 text-capitalize",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          this.actionPlanSelectedId = actionPlan.id;
          this.setActionPlanIdInSlotData({id_action_plan: this.actionPlanSelectedId});
        }
      });
    },
    setActionPlanIdInSlotData(data) {
      layoutApi.updateSlotData(this.slotInfo.pos, SLOT_TYPES.ACTION_PLAN, data);
    },
    onToggleClick() {
      this.isDocumentListVisible = !this.isDocumentListVisible;
    },
    onDragStart(widgetId, data) {
      this.setSelectedWidget(widgetId, data);

    },
    setSelectedWidget(widgetId, data) {
      switch (widgetId) {
        case "document_action_plan":
          this.setSlotData(SLOT_TYPES.DOCUMENT_ACTION_PLAN, `document_action_plan`, {data});
          break;
      }
    },
    onSearchInput: debounce(function () {
      this.searchDocuments();
    }, 500),
    async searchDocuments() {
      try {
        const params = {
          q: this.searchQuery,
          max: 10,
          'page[n]': 1,
          sortDesc: false,
          'id_tenant[]': this.slotData.tenant.id
        };

        const result = await this.$store.dispatch('action_plan_document/getAll', params);

        if (result && result.data) {
          this.documents = result.data;

          const tenantExists = this.documents.find(doc => doc.tenant_id === this.slotData.tenant.id);
          if (tenantExists) {
            this.areDocuments = true;
          } else {
            this.areDocuments = false;
          }
        } else {
          console.error('No data found in the result');
          this.areDocuments = false;
          this.documents = [];
        }

      } catch (error) {
        console.error('Error fetching action plan documents:', error);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../../../../../src/assets/scss/variables/variables';

.info-slot-container {
  height: 100%;
  max-height: 100%;
  overflow: auto;
  scroll-behavior: smooth;
  position: relative;
}

.action-plan-list {
  .action-plan-item {
    border: 1px solid rgb(112, 112, 112);
    padding: 3px;
    text-align: center;
    cursor: pointer;
    max-width: 175px;
  }
}

.action-plan-viewer-container {
  overflow: auto;
  padding-right: 5px;
}

.toggle-button {
  position: absolute;
  top: 10px; /* Adjust positioning as needed */
  right: 10px;
  background-color: #007bff;
  color: white;
  border: none;
  padding: 5px 10px;
  border-radius: 4px;
  cursor: pointer;
}

.toggle-button:hover {
  background-color: #0056b3;
}

.document-list {
  background-color: rgb(244, 244, 244);
  position: absolute;
  top: 40px;
  right: 10px;
  bottom: 50px;
  padding: 10px;
  max-height: 300px;
  overflow-y: auto;
  border-radius: 4px;
  border-color: black;
  border-width: 1px;
  width: 50%;
  color: black;
  border-style: solid;
  min-height: 50%;
  box-sizing: border-box;
  background-color: white;
}

.table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
}

.capitalize-first {
  text-transform: capitalize;
}

.dark-layout {
  .document-list {
    background-color: var(--second-dark) !important;
  }
}

.table th, .table td {
  padding: 10px;
  text-align: left;
  word-wrap: break-word;
}

tr {
  width: 100%;
}

</style>
